'use client';

import { CoveoSearchHub } from '@lib/coveo/coveoConstants';
import { fetchSearchToken } from '@lib/coveo/searchTokenHelpers';
import * as Sentry from '@sentry/nextjs';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { getLocaleFromUrl } from 'utils';
import { headerSearchContextValues } from '../headless/headerSearchContext';
import {
  HeaderStateProvider,
  browserNavigatorContext,
  createHeaderSearchEngineDefinition,
  type HeaderSearchEngine,
} from '../headless/headerSearchEngine';
import { ConnectedHeaderSearch } from './ConnectedHeaderSearch';
import { HeaderSearchProps } from './HeaderSearch.types';
import { StandaloneHeaderSearch } from './StandaloneHeaderSearch';
import { activeSearchQuery } from './searchRedirect';

/**
 * Global header search box on the SSR search path. Bootstraps the standalone
 * header engine in the browser — the header lives outside the results page's
 * provider and renders on pages that have no search engine at all — then swaps
 * the plain form for the suggestions-capable box.
 *
 * Until the engine is ready, and permanently if it never becomes ready, the
 * plain navigate-to-/search form renders: the header is always functional, and
 * the CSR path's disabled-until-connected input has no equivalent here.
 *
 * `build()` rather than `fetchStaticState()`/`hydrateStaticState()`: the
 * static-state path executes a first search (`engine.executeFirstSearch()`),
 * which would add a Coveo result query to every page view that renders the
 * header, for results nothing displays. The suggestion and redirect controllers
 * need no prior search. `build()` is deprecated in the SDK's SSR API — when it
 * goes, this needs a non-searching replacement, not fetchStaticState.
 */
export const HeaderSearch = ({
  placeholder,
  className,
  completionHint,
  completionAcceptedLabel,
  topSearchesLabel,
  trendingSearchesLabel,
  trending,
  onTrendingSlotShown,
}: HeaderSearchProps) => {
  const [engineState, setEngineState] = useState<HeaderSearchEngine | null>(
    null
  );
  // Show the query the results page is rendering, from the first server
  // render on: reading the route here rather than `window` in an effect is
  // what keeps the box from painting empty and filling at hydration. Every
  // route under app/[locale] renders dynamically (its layout reads the session
  // and draft mode), so useSearchParams costs no static rendering.
  //
  // Only the initial value: the shopper owns the box from then on, and the
  // results page rewrites its own URL on every facet, sort and page change.
  const pathname = usePathname() || '/';
  const searchParams = useSearchParams();
  // Held here rather than in either box: the plain form unmounts at the swap,
  // so what the box holds has to live in the component that outlives both.
  const [draft, setDraft] = useState(() =>
    activeSearchQuery(pathname, searchParams)
  );
  const [draftFocused, setDraftFocused] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const result = await fetchSearchToken(CoveoSearchHub.SEARCH, true);
        // Before building, not only after it: every build registers a callback
        // the definition never releases, so an engine built for a mount that is
        // already gone is retained for the rest of the session (Strict Mode
        // replays this effect on every mount in development).
        if (cancelled) {
          return;
        }
        if (!result?.token) {
          throw new Error('Header search token response carried no token');
        }

        const locale = getLocaleFromUrl();
        const definition = createHeaderSearchEngineDefinition(locale);
        definition.setAccessToken(result.token);
        // Client-only engine: it has no server component to marshal a navigator
        // context from, so supply a browser-derived one before building.
        definition.setNavigatorContextProvider(browserNavigatorContext);

        const built = await definition.build({
          controllers: {
            context: {
              initialState: {
                values: headerSearchContextValues(locale, result),
              },
            },
          },
        });
        if (!cancelled) {
          setEngineState(built);
        }
      } catch (error) {
        // The plain form stays on screen; only suggestions are lost.
        Sentry.withScope((scope) => {
          scope.setTags({
            'coveo.hub': CoveoSearchHub.SEARCH,
            'coveo.step': 'header_init',
          });
          Sentry.captureException(error);
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!engineState) {
    return (
      <StandaloneHeaderSearch
        placeholder={placeholder}
        className={className}
        value={draft}
        onValueChange={setDraft}
        onFocusChange={setDraftFocused}
      />
    );
  }

  return (
    <HeaderStateProvider
      engine={engineState.engine}
      controllers={engineState.controllers}
    >
      <ConnectedHeaderSearch
        placeholder={placeholder}
        className={className}
        initialQuery={draft}
        restoreFocus={draftFocused}
        completionHint={completionHint}
        completionAcceptedLabel={completionAcceptedLabel}
        topSearchesLabel={topSearchesLabel}
        trendingSearchesLabel={trendingSearchesLabel}
        trending={trending}
        onTrendingSlotShown={onTrendingSlotShown}
      />
    </HeaderStateProvider>
  );
};
